
import { Device } from '@capacitor/device';
import { CapacitorHttp } from '@capacitor/core';
import { authType, RHYBUS_ACCESS_TOKEN } from '../../../Configuration';
import { msalMobileSilentlyToken } from '../../utils/mobileMSAL';
import { loadConfig } from '../../../authConfig';
import { showLogoutPopup } from '../../../tokenUtils';



const formatHost = (host: string) => {
  if(window.location.host.indexOf('www.') === 0){
    return window.location.host.replace('www.','')
  }

  return host
}
export const siteHost = `${formatHost(window.location.host)}`
export const apiPath = process.env.REACT_APP_RHYBUS_API_URL + '/api'//"https://localhost:3001/api"//
const type = authType.toLowerCase();

// The platform can't change during a session, so resolve it once instead of making a
// Capacitor bridge call on every request. A failed lookup is not kept, so the next request retries.
let isMobilePlatformPromise: Promise<boolean> | null = null;
const isMobilePlatform = () => {
  if (!isMobilePlatformPromise) {
    isMobilePlatformPromise = Device.getInfo()
      .then((info) => ((info.operatingSystem === 'ios' || info.operatingSystem === 'android') && info.platform !== 'web'))
      .catch((error) => {
        isMobilePlatformPromise = null;
        throw error;
      });
  }
  return isMobilePlatformPromise;
};

const selectMethod = async (url: string, options: any) => {
  if (await isMobilePlatform()) {
    return mobileFetch(url, options);
  } else {
   // @rhybus/api reads the token/session id from localStorage - supply them from sessionStorage instead
   const res = await fetch(url, options);
    if (!res.ok) {
      let body = null; 
        const text = await res.text();

        if (text.trim() !== "") {
          try {
            body = JSON.parse(text);
          } catch (err) {
            console.error("Invalid JSON from server:", text);
          }
        }
        if (type === 'otp' && (res.status === 401 || res.status === 403)) {
          if(body !== null && body?.code === "URC_401"){
           showLogoutPopup("User Role Changed", "Your user roles have changed. Please log in again and continue.");
          }else if(body !== null && body?.code === "ACD_403"){
           showLogoutPopup("Access Deactivated", "Your access has been deactivated from the Anew Transport TRM.");
          }else{
            showLogoutPopup("Session Expired", "Your session has expired. Please log in again.");
          }
        }  
    }
    return res
  }
}

const mobileFetch = async (url: string, options: any, isRetry = false) => {
  const { mobileBaseURL, clientcode } =   await loadConfig();
  let bearerToken = localStorage.getItem(RHYBUS_ACCESS_TOKEN);
  const apiURL = `${mobileBaseURL}${url}`;
  let headers:Record<string,any> = {
    Authorization: `Bearer ${bearerToken}`,
    'Content-Type': 'application/json',
  };
  
  try {
    if (url.includes('list/blobaddorupdate')) {
      // Standard fetch for FormData cases
      const response = await fetch(apiURL, { ...options, headers:{
        Authorization: `Bearer ${bearerToken}`,
      } });
      if (response.status === 401 && !isRetry) {
      if(type === 'msal'){
        await msalMobileSilentlyToken();
        return mobileFetch(url, options, true);
      }
      }
      return response;
    }
    let requestData: any = undefined;
    const isBinaryResponse = url.includes('blobgetcontent');
    if(options.method === 'POST' && (url.includes("/login/loginotp") || url.includes('/login/verifyloginotp')  || url.includes('login/register') ||  url.includes('/login/verifyregisterotp'))){
     headers={
      clientcode:clientcode,
      'Content-Type': 'application/json',
     }
     const uint8 = new Uint8Array(options.body);
      const decoder = new TextDecoder('utf-8');
      const jsonString = decoder.decode(uint8);
      requestData = JSON.parse(jsonString);
    }
    else if(options.method === 'POST' || options.method === 'PUT' || (url.includes('blobdelete') && options.method === 'DELETE')) {
      const uint8 = new Uint8Array(options.body);
      const decoder = new TextDecoder('utf-8');
      const jsonString = decoder.decode(uint8);
      requestData = JSON.parse(jsonString);
    }
    const response = await CapacitorHttp.request({
      url: apiURL,
      method: options.method,
      headers,
      data: typeof(requestData)==='string'?`"${requestData}"`:requestData,
      responseType: isBinaryResponse ? 'arraybuffer' : 'json', // ✅ Key fix
    });


    // If token expired and not retried yet, refresh token and retry
    if ((response.status === 401 || response.status === 403) && !isRetry) {
      if(type === 'msal'){
      await msalMobileSilentlyToken();
      return mobileFetch(url, options, true);
      }else{
       const errorData = await response.data;
        if(errorData.code === "URC_401"){
        showLogoutPopup("User Role Changed", "Your user roles have changed. Please log in again and continue.");
        }else if(errorData.code === "ACD_403"){
        showLogoutPopup("Access Deactivated", "Your access has been deactivated from the Anew Transport TRM.");
        }else{
        showLogoutPopup("Session Expired", "Your session has expired. Please log in again.");
        }
      }
    }
    // 🧩 Handle file / binary response
    if (isBinaryResponse) {
      const base64Data = response.data;
      const contentType =
        response.headers?.['content-type'] || 'application/octet-stream';
      const binaryData = base64ToArrayBuffer(base64Data);

      return new Response(binaryData, {
        status: response.status,
        headers: { 'Content-Type': contentType },
      });
    }
    else
    {
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  } catch (error) {
    console.error('Fetch failed:', error);
  }
};

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}


export const fetchProvider = {
  Headers,
  Request,
  Response,
  fetch: async (url: string, options: any) => await selectMethod(url, options),
};



